import React from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { BarList, EmptyState, MetricCard, Panel, ToolbarButton, type MetricTrend } from '../../../ui-kit';
import { fetchWaSpotlight, type WaDay, type WaSpotlight } from '../../../services/websiteAssistantService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { useLive } from '../rocco/live';
import { AgencyZoomMark } from '../missed-call/logos';
import { useAgent } from '../AgentContext';

const whole = (v: number) => v.toLocaleString();

/**
 * The page the Website Assistant opens on: how a visitor moves through it, the
 * numbers so far, what visitors come for, and the chat itself, one click away.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { base } = useAgent();
  const { data, error } = useLive<WaSpotlight>(fetchWaSpotlight, 15_000);
  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const totals = data?.tiles.totals;

  const nodes: FlowNode[] = [
    { id: 'site', step: 'A visitor opens the chat', name: 'Your website',
      detail: 'Any page, any time', logo: <LanguageRounded sx={{ fontSize: 20 }} />, to: 'live' },
    { id: 'chat', step: 'We ask what we need', name: 'The chat',
      detail: 'Name, cars, drivers, current insurance', logo: <ForumRounded sx={{ fontSize: 20 }} />, to: 'live' },
    { id: 'docs', step: 'Documents are read', name: 'Dec page or licence',
      detail: 'Details filled in, never asked twice', logo: <DescriptionOutlined sx={{ fontSize: 20 }} />, to: 'completed' },
    { id: 'az', step: 'The lead is ready to quote', name: 'AgencyZoom',
      detail: 'With every answer and document', logo: <AgencyZoomMark />, to: 'completed' },
  ];

  const trend = (points: WaDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  return (
    <Stack spacing={2}>
      <AgentFlow nodes={nodes} loading={loading}
        subtitle="From a visitor on your website to a lead in AgencyZoom, ready to quote"
        onOpen={(to) => navigate(`${base}/${to}`)} />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <MetricCard loading={loading} label="Chats" value={whole(totals?.chats ?? 0)}
          info="Every visitor who opened the chat on the website."
          trend={trend(data?.tiles.chats ?? [], 'Chats by day, this week')} />
        <MetricCard loading={loading} label="Leads in AgencyZoom" value={whole(totals?.leads ?? 0)}
          note={totals?.leads_from_documents ? `${whole(totals.leads_from_documents)} filled in from documents` : undefined}
          info="Auto quote requests sent to AgencyZoom as leads, with every answer and document on them."
          trend={trend(data?.tiles.leads ?? [], 'Leads by day, this week')} />
        <MetricCard loading={loading} label="Questions answered" value={whole(totals?.answered ?? 0)}
          info="Clients who asked about their own policy and were answered from its records." />
        <MetricCard loading={loading} label="Passed to the team" value={whole(totals?.handed ?? 0)}
          info="Questions the records could not settle, or visitors who asked for a person. Each is a call-back task in AgencyZoom." />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What visitors come for" subtitle="Every chat so far" loading={loading} loadingHeight={140}>
          <BarList rows={(data?.by_intent ?? []).map((r) => ({ key: r.label, label: r.label, value: r.value }))}
            formatValue={whole} valueLabel="Chats" labelHeading="Came for" />
        </Panel>
        <Panel title="The chat" subtitle="As a visitor on your website sees it">
          <Stack spacing={1.5} alignItems="flex-start">
            <ToolbarButton variant="brand" icon={<OpenInNewRounded />}
              onClick={() => data && window.open(data.widget_path, '_blank', 'noopener')} disabled={!data}>
              Open the chat
            </ToolbarButton>
          </Stack>
        </Panel>
      </Box>
    </Stack>
  );
}
